define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../Util/numberArray2String',
    '../Util/colorArray2String',
    '../Util/string2NumberArray',
    '../Util/string2ColorArray',
    '../Util/json2Array',
    '../Util/array2Json',
    '../System/ColorObj',
    '../System/Vec3Obj',
    '../System/CircleObj'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    numberArray2String,
    colorArray2String,
    string2NumberArray,
    string2ColorArray,
    json2Array,
    array2Json,
    ColorObj,
    Vec3Obj,
    CircleObj
) {
    'use strict'

    /**
     * 多重动态圈。
     * <br /><br />
     * <div align='center'>
     * <img src='images/MultipleCircles.gif' width='900' height='650' /><br />
     * 地理围栏效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/MultipleCircles.html|CooWasm MultipleCircles demo}
     * @alias MultipleCircles
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {Vec3Obj} [props.point=null] 圆心位置，当为空时，可用鼠标在场景中点击确定圆心。
     * @param {String} [props.imageUrl="texture/dotted_circle_1024.png"] 动态圈纹理路径，可传入网络路径。
     * @param {Array.<CircleObj>} [props.circleArr=[new CircleObj(100, "#FF0000", "Circle", "#FF0000")]] 动态圈对象数组。
     * @param {Number} [props.speed=10.0] 动态圈角速度，单位：角度/秒。
     * @param {Number} [props.scanAngle=90] 扫描角度。
     * @param {Number} [props.transparency=80.0] 最大渐变透明度，范围0-100。
     * @param {Number} [props.textSize=32] 显示文字大小。
     * @param {String} [props.fontUrl="Fonts/msyh.ttf"] 默认字体路径，可传入网络路径。
     * @param {Number} [props.minVisibleDistance=1.0] 最小显示距离，单位：米。
     * @param {Number} [props.maxVisibleDistance=10000.0] 最大显示距离，单位：米。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @example
     * var MultipleCircles = new CooWasm.MultipleCircles({},(state)=>{console.log("load status:" + state)});
     */
    class MultipleCircles {
        /**
         * @description 圆心位置，当为空时，可用鼠标在场景中点击确定圆心。
         * @type {Vec3Obj}
         */
        point
        /**
         * @description 动态圈纹理路径，可传入网络路径。
         * @type {String}
         */
        imageUrl
        /**
         * @description 动态圈对象数组。
         * @type {Array.<CircleObj>}
         */
        circleArr
        /**
         * @description 动态圈角速度，单位：角度/秒。
         * @type {Number}
         */
        speed
        /**
         * @description 扫描角度。
         * @type {Number}
         */
        scanAngle
        /**
         * @description 最大渐变透明度，范围0-100。
         * @type {Number}
         */
        transparency
        /**
         * @description 显示文字大小。
         * @type {Number} 
         */
        textSize
        /**
         * @description 默认字体路径，可传入网络路径。
         * @type {String}
         */
        fontUrl
        /**
         * @description 最小显示距离，单位：米。
         * @type {Number}
         */
        minVisibleDistance
        /**
         * @description 最大显示距离，单位：米。
         * @type {Number}
         */
        maxVisibleDistance
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
         * @description 显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true

        /**
         * @description 地理围栏对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 地理围栏组件uuid。
         * @type {String}
         * @readonly
         */
        componentMultipleCirclesUuid = null
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description GeoFence在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "MultipleCircles"
        static avatarType = "deSMP.Avatar.EmptyAvatar"
        static componentMultipleCirclesType = "deSMP.AvatarComponent.MultipleCirclesComponent"
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            let bSync = true
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.EmptyAvatar"
            let componentCategoryName = MultipleCircles.componentMultipleCirclesType + "#$%" + MultipleCircles.componentAltitudeVisCtrlType + "#$%"
            let avatarProps = ""
            let componentProps = ""
            //avatar props
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
            //component props
            let multipleCirclesComponentProps = ""
            if (defined(props.point)) {
                multipleCirclesComponentProps += this.toTuple3Str("point", props.point, "Point")
            }
            if (defined(props.circleArr)) {
                this.circleArr = props.circleArr
                multipleCirclesComponentProps += "CircleArray:" + array2Json('COMPOSITE', this.circleArr) + "|"
            } else {
                this.circleArr = [new CircleObj(100, "#FF0000", "Circle", "#FF0000")]
                multipleCirclesComponentProps += "CircleArray:" + array2Json('COMPOSITE', this.circleArr) + "|"
            }
            multipleCirclesComponentProps += this.toStr("imageUrl", props.imageUrl, "ImageUrl")
            multipleCirclesComponentProps += this.toStr("speed", props.speed, "Speed")
            multipleCirclesComponentProps += this.toStr("scanAngle", props.scanAngle, "ScanAngle")
            multipleCirclesComponentProps += this.toStr("transparency", props.transparency, "MaxTransparency")
            multipleCirclesComponentProps += this.toStr("textSize", props.textSize, "TextSize")
            multipleCirclesComponentProps += this.toStr("fontUrl", props.fontUrl, "TextFontUrl")
            multipleCirclesComponentProps += this.toStr("minVisibleDistance", props.minVisibleDistance, "MinVisibleDistance")
            multipleCirclesComponentProps += this.toStr("maxVisibleDistance", props.maxVisibleDistance, "MaxVisibleDistance")
            componentProps += multipleCirclesComponentProps + "#$%"
            let altitudeVisCtrlComponentProps = ""
            altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
            altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")
            componentProps += altitudeVisCtrlComponentProps + "#$%"

            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(componentCategoryName), encode(componentProps), bSync, this.bVisible, true)
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentMultipleCirclesUuid = idArray[1]
                this.componentAltitudeVisCtrlUuid = idArray[2]
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    if (defined(this.point)) {
                        this.getProperties()
                        if (defined(callback)) {
                            callback(state)
                        }
                        Element.prototype.Map.multipleCircles.push(this)

                    } else {
                        callbackSystem.register("EditEnd", this.avatarUuid, (state) => {
                            this.getProperties()
                            if (defined(callback)) {
                                callback(state)
                            }
                            Element.prototype.Map.multipleCircles.push(this)
                        })
                    }
                })
            }
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} props 包含如下属性的Object：
         * @param {Vec3Obj} [props.point] 圆心位置，当为空时，可用鼠标在场景中点击确定圆心。
         * @param {String} [props.imageUrl] 动态圈纹理路径，可传入网络路径。
         * @param {Array.<CircleObj>} [props.circleArr] 动态圈对象数组。
         * @param {Number} [props.speed] 动态圈角速度，单位：角度/秒。
         * @param {Number} [props.scanAngle] 扫描角度。
         * @param {Number} [props.transparency] 最大渐变透明度，范围0-100。
         * @param {Number} [props.textSize] 显示文字大小。
         * @param {String} [props.fontUrl] 默认字体路径，可传入网络路径。
         * @param {Number} [props.minVisibleDistance] 最小显示距离，单位：米。
         * @param {Number} [props.maxVisibleDistance] 最大显示距离，单位：米。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {CallbackBoolean} [callback] 加载成功后触发的函数回调。
         * @example
         * MultipleCircles.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            let multipleCirclesComponentProps = ""
            let altitudeVisCtrlComponentProps = ""
            let uuids = ""
            if (defined(props)) {
                avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
                //multipleCirclesComponent
                if (defined(props.point)) {
                    multipleCirclesComponentProps += this.toTuple3Str("point", props.point, "Point")
                }
                if (defined(props.circleArr)) {
                    this.circleArr = props.circleArr
                    multipleCirclesComponentProps += "CircleArray:" + array2Json('COMPOSITE', this.circleArr) + "|"
                }
                multipleCirclesComponentProps += this.toStr("imageUrl", props.imageUrl, "ImageUrl")
                multipleCirclesComponentProps += this.toStr("speed", props.speed, "Speed")
                multipleCirclesComponentProps += this.toStr("scanAngle", props.scanAngle, "ScanAngle")
                multipleCirclesComponentProps += this.toStr("transparency", props.transparency, "MaxTransparency")
                multipleCirclesComponentProps += this.toStr("textSize", props.textSize, "TextSize")
                multipleCirclesComponentProps += this.toStr("fontUrl", props.fontUrl, "TextFontUrl")
                multipleCirclesComponentProps += this.toStr("minVisibleDistance", props.minVisibleDistance, "MinVisibleDistance")
                multipleCirclesComponentProps += this.toStr("maxVisibleDistance", props.maxVisibleDistance, "MaxVisibleDistance")
                //altitudeVisCtrlComponent
                altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
                altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")

                uuids += this.componentMultipleCirclesUuid + "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"
                componentProps += multipleCirclesComponentProps + "#$%"
                componentProps += altitudeVisCtrlComponentProps + "#$%"

                callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }

        toTuple3Str(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                const posStr = `{"x":${value.x},"y":${value.y},"z":${value.z}}`
                result = key + ":" + posStr + "|"
            }
            return result
        }
        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * MultipleCircles.locate((state)=>{console.log("locate status:" + state)});
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * MultipleCircles.getProperties();
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                if (pName == "MultipleCirclesComponent") {
                    this.componentMultipleCirclesUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "Point") {
                            this.point = Vec3Obj.fromStr(value)
                        } else if (name == "ImageUrl") {
                            this.imageUrl = value
                        } else if (name == "CircleArray") {
                            this.circleArr = json2Array(value)
                        } else if (name == "Speed") {
                            this.speed = Number(value)
                        } else if (name == "ScanAngle") {
                            this.scanAngle = Number(value)
                        } else if (name == "MaxTransparency") {
                            this.transparency = Number(value)
                        } else if (name == "TextSize") {
                            this.textSize = Number(value)
                        } else if (name == "TextFontUrl") {
                            this.fontUrl = value
                        } else if (name == "MinVisibleDistance") {
                            this.minVisibleDistance = Number(value)
                        } else if (name == "MaxVisibleDistance") {
                            this.maxVisibleDistance = Number(value)
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁对象后触发的函数回调。
         * @returns null 空值。
         * @example
         * MultipleCircles = MultipleCircles.destroy((state)=>{console.log("destroy status:" + state)});
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.multipleCircles = Element.prototype.Map.multipleCircles.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("EditEnd", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentMultipleCirclesUuid = null
                this.componentAltitudeVisCtrlUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return MultipleCircles
})